import React, { useEffect, useState } from 'react'
import CustomHeader from '../../components/CustomHeader'
import PAGE_TITLES from '../../consts/titles'
import SurveyListFeature from '../../features/StudentSurveyListFeature'
import EmptyListCard from '../../features/StudentSurveyListFeature/components/EmptyListCard'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import SkipNavigation from '../../components/SkipNavigation'
import LoadingPageV2 from '../LoadingPageV2'
import PageHeader from '../../components/PageHeader'
import CustomPagination from '../../components/CustomTable/CustomPagination'
import GenericButton from '../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../components/GenericButton/styles'
import { useUserProfile } from '../../hooks/useUserProfile'
import { STUDENT_CARDS_PAGE_SIZE, useStudentDistributions } from '../../hooks/useStudentDistributions'
import { grey } from '../../colors'
import { notifyError } from '../../utils/notifyError'

const StudentDashboardPage: React.FC = (): JSX.Element => {
  const { data: userProfile } = useUserProfile()
  const user = userProfile?.user
  const studentName = user?.name

  // Paginated to match the instructor dashboard (CR-293). Unlike that surface there is no term
  // selector, so the page number needs no scoping — there is exactly one list per student.
  const [pageNumber, setPageNumber] = useState(1)

  const {
    surveys,
    lastPagination,
    totalResults,
    isLoading,
    isError,
    error
  } = useStudentDistributions(pageNumber)

  // The hook marks this query `meta.silent`, so the generic global toast does not fire and this is
  // the only one — with the wording the deleted Redux slice used. Notifying without the opt-out
  // would show two toasts, because the global subscriber does not de-duplicate against callers.
  useEffect(() => {
    if (isError) notifyError(error, { fallbackMessage: 'Failed to load surveys. Please try again.' })
  }, [isError, error])

  const renderContent = (): JSX.Element => {
    // Inside the shell rather than an early return, so a page change does not unmount
    // SkipNavigation, the page title, and the pager on its way to the next page.
    if (isLoading) {
      // The whole-page loader is for the first load only, where there is nothing on screen worth
      // preserving. On a page change it would undo the very thing the placement above achieves:
      // `LoadingPageV2` renders a fixed, opaque, 100vw/100vh box at zIndex 9999, so the pager stays
      // mounted but goes invisible along with the app bar — and after ten seconds it offers the
      // student a "Log out" button for what is a routine page fetch. The admin list uses an inline
      // spinner inside its list area for exactly this reason.
      if (lastPagination == null) {
        return <LoadingPageV2 title={PAGE_TITLES.STUDENT_SURVEYS} />
      }
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height={400} mt={2}>
          <CircularProgress />
        </Box>
      )
    }

    // Not the empty state: with no data the card list would say "no surveys", which asserts
    // something false about this student when the request simply failed. Same distinction as
    // BC-7 on the instructor dashboard.
    if (isError) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={8} textAlign="center">
          <Typography fontSize="16px" fontWeight={500} color={grey[950]}>
            We couldn&apos;t load {pageNumber > 1 ? `page ${pageNumber} of your surveys` : 'your surveys'}.
          </Typography>
          {pageNumber > 1 && (
            <GenericButton
              buttonType={ButtonType.Secondary}
              buttonSize={ButtonSize.Medium}
              label="Back to first page"
              onClick={() => { setPageNumber(1) }}
            />
          )}
        </Box>
      )
    }

    if (surveys.length === 0) {
      // Past page 1, "no surveys" would be asserting something false: the list shrank under a
      // remembered page number, so this page is empty while earlier ones are not. The pager hides
      // itself once `numPages` drops to 1, which would leave no way back — so the escape lives
      // here, the same shape as the failed-page arm above.
      if (pageNumber > 1) {
        return (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={8} textAlign="center">
            <Typography fontSize="16px" fontWeight={500} color={grey[950]}>
              There are no surveys on page {pageNumber} any more.
            </Typography>
            <GenericButton
              buttonType={ButtonType.Secondary}
              buttonSize={ButtonSize.Medium}
              label="Back to first page"
              onClick={() => { setPageNumber(1) }}
            />
          </Box>
        )
      }
      return (
        <React.Fragment>
          <PageHeader
            title={studentName}
            pageTitleAriaLabel={`Page title ${studentName ?? ''}`}
            isCompactOnly
          />
          <EmptyListCard />
        </React.Fragment>
      )
    }

    return (
      <SurveyListFeature
        list={surveys}
        studentName={studentName ?? ''}
        pageNumber={pageNumber}
        // The size the server actually applied, not the size we asked for: it drives the announced
        // "Survey N of M" offset, so if the backend ever clamps `page_size` the constant would
        // announce the wrong numbers on every page after the first.
        pageSize={lastPagination?.pageSize ?? STUDENT_CARDS_PAGE_SIZE}
        totalResults={totalResults}
      />
    )
  }

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
    >
      <SkipNavigation />
      <Grid item>
        <CustomHeader title={PAGE_TITLES.STUDENT_SURVEYS} />
        {renderContent()}
        {/* Only when there is more than one page: a student with a handful of surveys should see
            the list exactly as they do today.
            Reads the last known pagination so a failed page keeps the pager reachable rather than
            stranding the student on the page that failed — same as the other two dashboards. */}
        {lastPagination != null && lastPagination.numPages > 1 && (
          <CustomPagination
            numPages={lastPagination.numPages}
            pageNumber={pageNumber}
            pageSize={lastPagination.pageSize}
            totalResults={lastPagination.totalResults}
            handlePageChange={(page: number) => { setPageNumber(page) }}
            showPageSizeOptions={false}
          />
        )}
      </Grid>
    </Grid>
  )
}

export default StudentDashboardPage
